import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MessageSquare, Send } from "lucide-react";
import ChatName from "@/components/ChatName";

export default function GuildChat() {
  const [msgs, setMsgs] = useState([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const refresh = async () => {
    try { setMsgs(await api.guildChatGet()); } catch (e) { /* silent */ }
  };
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs.length]);

  const send = async () => {
    if (!msg.trim() || busy) return;
    setBusy(true);
    try {
      const res = await api.guildChatSend(msg.trim());
      if (res && res.ok === false && res.discipline) {
        const d = res.discipline;
        toast[d.muted ? "error" : "message"](d.reason || `Warning ${d.warning_count}/3`, { duration: 4000 });
      }
      setMsg("");
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="panel" data-testid="guild-chat">
      <div className="px-3 py-2 border-b border-[#262626] flex items-center gap-2">
        <MessageSquare className="w-3 h-3 text-[#4A7BC7]" strokeWidth={1.5} />
        <span className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c]">
          Guild Hall
        </span>
        <span className="ml-auto font-mono text-[10px] text-[#666]">private</span>
      </div>
      <div className="max-h-40 overflow-y-auto p-3 space-y-1 text-sm">
        {msgs.length === 0 && (
          <p className="italic text-[#666] text-xs">The hall is silent. Break it.</p>
        )}
        {msgs.map((c, i) => (
          <div key={i} className="font-body text-xs" data-testid={c.is_admin ? "chat-msg-admin" : undefined}>
            {c.kind === "emote" ? (
              <>
                <ChatName name={c.name} isAdmin={c.is_admin} isPreAlpha={c.is_pre_alpha} />{" "}
                <span className="italic text-[#B89C3A]">{c.text}</span>
              </>
            ) : (
              <>
                <ChatName name={c.name} isAdmin={c.is_admin} isPreAlpha={c.is_pre_alpha} colon />{" "}
                <span className="text-[#B0ADA5]">{c.text}</span>
              </>
            )}
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div className="flex border-t border-[#262626]">
        <input
          data-testid="guild-chat-input"
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Speak to your kin..."
          maxLength={200}
          className="flex-1 bg-transparent px-3 py-2 text-sm text-[#E2DFD8] outline-none placeholder-[#555]"
        />
        <button
          data-testid="guild-chat-send"
          onClick={send}
          disabled={busy || !msg.trim()}
          className="px-3 border-l border-[#262626] text-[#4A7BC7] hover:bg-[#1a1a1a] disabled:opacity-30"
        >
          <Send className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}
